import { useState } from 'react';
import { Link } from 'react-router';
import { Mail, Phone, Clock, User, ChevronRight } from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// TODO: Replace with your n8n webhook URL once configured
const N8N_WEBHOOK_URL = 'https://casunlimited.app.n8n.cloud/webhook/contact-form';

const enquiryOptions = [
  'General Inquiry',
  'Project Quote',
  'Technical Support',
  'Partnership',
];

export default function Contact() {
  useDocumentTitle('Contact Us — Straton Group');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    enquiry_type: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!response.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email us directly at info@stratongroup.co.za');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content">
      {/* ── Hero Section ── */}
      <section className="relative min-h-[60vh] lg:min-h-[70vh] flex items-end">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/contact-hero.webp" fetchPriority="high"
            alt="Straton Group facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/80 to-bg-primary/40" />
        </div>

        {/* Text content */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto section-padding pb-16 lg:pb-24">
          {/* Label with line */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              GET IN TOUCH
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-[0.15em] text-text-primary mb-8">
            CONTACT US
          </h1>

          <p className="text-sm md:text-base uppercase tracking-[0.15em] text-text-secondary mb-6 max-w-xl">
            WE ARE HERE TO BUILD SOLUTIONS THAT MOVE INDUSTRY FORWARD.
          </p>

          <p className="body-text max-w-lg">
            Whether you have a project enquiry, need technical support, or want to join our network of skilled professionals, we would like to hear from you.
          </p>
        </div>
      </section>

      {/* ── Form + Contact Info ── */}
      <section className="py-16 lg:py-24 bg-bg-primary section-padding">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border-secondary">
            {/* Left — Form */}
            <div className="bg-bg-secondary p-8 lg:p-12">
              <h2 className="heading-sm text-text-primary mb-2">SEND US A MESSAGE</h2>
              <div className="w-8 h-px bg-text-muted mb-10" />

              {submitted ? (
                <div className="bg-bg-primary border border-border-secondary p-10 text-center">
                  <h3 className="heading-md text-text-primary mb-3">MESSAGE SENT</h3>
                  <p className="body-text">
                    Thank you for reaching out. Our team will respond to your enquiry within 24 business hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="border border-red-900/50 bg-red-950/20 p-4 text-xs text-red-300">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="sr-only">Full Name</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="FULL NAME"
                        className="w-full bg-bg-primary border border-border-secondary text-text-primary px-4 py-3.5 text-xs uppercase tracking-widest placeholder:text-text-muted focus:border-text-muted outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="sr-only">Email Address</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="EMAIL ADDRESS"
                        className="w-full bg-bg-primary border border-border-secondary text-text-primary px-4 py-3.5 text-xs uppercase tracking-widest placeholder:text-text-muted focus:border-text-muted outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-company" className="sr-only">Company</label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="COMPANY (OPTIONAL)"
                      className="w-full bg-bg-primary border border-border-secondary text-text-primary px-4 py-3.5 text-xs uppercase tracking-widest placeholder:text-text-muted focus:border-text-muted outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className="sr-only">Phone Number</label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="PHONE NUMBER"
                      className="w-full bg-bg-primary border border-border-secondary text-text-primary px-4 py-3.5 text-xs uppercase tracking-widest placeholder:text-text-muted focus:border-text-muted outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-enquiry" className="sr-only">Enquiry Type</label>
                    <select
                      id="contact-enquiry"
                      name="enquiry_type"
                      required
                      value={form.enquiry_type}
                      onChange={handleChange}
                      className="w-full bg-bg-primary border border-border-secondary text-text-primary px-4 py-3.5 text-xs uppercase tracking-widest appearance-none focus:border-text-muted outline-none transition-colors"
                    >
                      <option value="" className="text-text-muted">ENQUIRY TYPE</option>
                      {enquiryOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="sr-only">Message</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={2000}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="MESSAGE"
                      className="w-full bg-bg-primary border border-border-secondary text-text-primary px-4 py-3.5 text-xs uppercase tracking-widest placeholder:text-text-muted focus:border-text-muted outline-none transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-text-primary text-bg-primary text-xs font-medium uppercase tracking-widest hover:bg-text-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{loading ? 'SENDING...' : 'SEND MESSAGE'}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <p className="text-[10px] text-text-muted leading-relaxed">
                    Your information is secure and will only be used to respond to your enquiry.
                  </p>
                </form>
              )}
            </div>

            {/* Right — Contact Info */}
            <div className="bg-bg-secondary p-8 lg:p-12">
              <h2 className="heading-sm text-text-primary mb-2">CONTACT INFORMATION</h2>
              <div className="w-8 h-px bg-text-muted mb-10" />

              <div className="space-y-0">
                {/* Email */}
                <div className="flex items-start gap-5 py-6 border-b border-border-secondary">
                  <Mail className="w-5 h-5 text-text-muted shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted block mb-1">
                      EMAIL
                    </span>
                    <a href="mailto:info@stratongroup.co.za" className="text-sm text-text-primary hover:text-text-secondary transition-colors">
                      info@stratongroup.co.za
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-5 py-6 border-b border-border-secondary">
                  <Phone className="w-5 h-5 text-text-muted shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted block mb-1">
                      PHONE
                    </span>
                    <a href="tel:+27415073200" className="text-sm text-text-primary hover:text-text-secondary transition-colors">
                      +27 41 507 3200
                    </a>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-5 py-6">
                  <Clock className="w-5 h-5 text-text-muted shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted block mb-1">
                      BUSINESS HOURS
                    </span>
                    <span className="text-sm text-text-primary">
                      Mon – Fri: 07:30 – 16:30 SAST
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Careers CTA Banner ── */}
      <section className="bg-bg-primary section-padding pb-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="border border-border-secondary p-8 lg:p-10 flex flex-col lg:flex-row items-center gap-8">
            {/* Icon */}
            <div className="shrink-0">
              <User className="w-8 h-8 text-text-muted" strokeWidth={1.5} />
            </div>

            {/* Text */}
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-text-primary mb-2">
                LOOKING TO JOIN OUR TEAM?
              </h3>
              <p className="body-sm">
                Explore career opportunities and become part of our global network of skilled professionals.
              </p>
            </div>

            {/* Button */}
            <Link
              to="/careers"
              className="shrink-0 inline-flex items-center gap-3 px-8 py-3.5 border border-border-primary text-text-secondary text-xs font-medium uppercase tracking-widest hover:border-text-muted hover:text-text-primary transition-colors"
            >
              <span>VIEW CAREERS</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
